import { supabase } from './supabase';
import type { UserRole } from './auth';

export interface UserRecord {
  id: string;
  displayName: string;
  role: UserRole;
  createdAt: string;
}

async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session;
}

export async function listUsers(): Promise<UserRecord[]> {
  const session = await getSession();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/list-users`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      Apikey: anonKey,
    },
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to fetch users');
  }

  return res.json();
}

export async function createUser(data: {
  displayName: string;
  password: string;
  role: UserRole;
}): Promise<void> {
  const session = await getSession();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  const res = await fetch(`${supabaseUrl}/functions/v1/admin-create-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to create user');
  }
}

export async function updateUserProfile(
  userId: string,
  updates: { role?: UserRole; displayName?: string; password?: string }
): Promise<void> {
  const session = await getSession();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  const res = await fetch(`${supabaseUrl}/functions/v1/update-user-role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ userId, ...updates }),
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to update user');
  }
}
